import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '@/i18n';

const TeachStepsSection: React.FC = () => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const stories = [
    {
      quote: isZh
        ? '以前总觉得找学生很不稳定。Tutor Founder 让整个流程从一开始就更清晰、更快，也更让人安心。'
        : 'Finding the right students used to feel inconsistent. Tutor Founder made the process clear, fast, and reassuring from the start.',
      name: 'Andy',
      role: isZh ? '国际学校现职教师' : 'Current International School Teacher',
      initials: 'L',
      avatarClass: 'bg-[linear-gradient(145deg,#d9e9df_0%,#f7faf5_100%)] text-primary',
    },
    {
      quote: isZh
        ? '我希望更稳定地教学，而不是每周都花时间找新个案。这个平台让我把注意力放回课堂，而不是行政事务。'
        : 'I wanted to teach more often without spending every week searching for new cases. The platform helped me focus on lessons, not admin.',
      name: 'Amira',
      role: isZh ? '中学数学导师' : 'Secondary Mathematics Tutor',
      initials: 'A',
      avatarClass: 'bg-[linear-gradient(145deg,#f2efd0_0%,#fbfaf0_100%)] text-primary',
    },
    {
      quote: isZh
        ? 'Tutor Founder 让我的档案看起来更专业。家长能更快理解我的优势，配对品质也明显更好。'
        : 'Tutor Founder gave my profile a more professional presence. Parents understood my strengths quickly, and the matches felt much better.',
      name: 'Daniel',
      role: isZh ? '科学与 IGCSE 导师' : 'Science and IGCSE Tutor',
      initials: 'D',
      avatarClass: 'bg-[linear-gradient(145deg,#e7eee0_0%,#fafcf7_100%)] text-primary',
    },
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const activeStory = stories[activeIndex];

  const showPreviousStory = () => {
    setActiveIndex((current) => (current === 0 ? stories.length - 1 : current - 1));
  };

  const showNextStory = () => {
    setActiveIndex((current) => (current === stories.length - 1 ? 0 : current + 1));
  };

  return (
    <section id="how-it-works" className="scroll-mt-28 bg-white py-12 md:py-16">
      <div className="container mx-auto max-w-6xl px-4 md:px-8 lg:px-12 xl:px-16">
        <div className="mb-6 flex items-center justify-between gap-6 md:mb-8">
          <h2 className="text-4xl font-bold leading-tight tracking-tight text-black md:text-5xl">
            {isZh ? '成功案例' : 'Successful story'}
          </h2>
          <div className="hidden h-px w-56 bg-highlight/70 md:block" />
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={showPreviousStory}
            aria-label={isZh ? '查看上一则成功案例' : 'Show previous success story'}
            className="absolute left-0 top-1/2 z-10 hidden h-12 w-12 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-primary shadow-md ring-1 ring-primary-100 transition-all duration-200 hover:-translate-x-[55%] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary md:flex"
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={2.75} />
          </button>

          <article className="flex min-h-[320px] flex-col items-center justify-center rounded-[1.5rem] bg-[linear-gradient(145deg,#f8faf6_0%,#ffffff_48%,#f4f7ef_100%)] px-6 py-12 text-center shadow-sm ring-1 ring-primary-50 md:min-h-[360px] md:rounded-[2rem] md:px-16">
            <p className="mx-auto max-w-3xl font-serif text-xl font-semibold leading-snug text-primary sm:text-2xl md:text-3xl md:leading-tight">
              {activeStory.quote}
            </p>

            <div className="mt-10 flex items-center gap-4 text-left">
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold shadow-sm ring-1 ring-primary-100 ${activeStory.avatarClass}`}
                aria-hidden="true"
              >
                {activeStory.initials}
              </div>
              <div>
                <p className="text-sm font-bold text-primary md:text-base">{activeStory.name} —</p>
                <p className="mt-0.5 text-xs font-semibold text-gray-500 md:text-sm">{activeStory.role}</p>
              </div>
            </div>
          </article>

          <button
            type="button"
            onClick={showNextStory}
            aria-label={isZh ? '查看下一则成功案例' : 'Show next success story'}
            className="absolute right-0 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-primary shadow-md ring-1 ring-primary-100 transition-all duration-200 hover:translate-x-[55%] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary md:flex"
          >
            <ChevronRight className="h-6 w-6" strokeWidth={2.75} />
          </button>
        </div>

        <div className="mt-5 flex items-center justify-center gap-3 md:mt-6">
          <button
            type="button"
            onClick={showPreviousStory}
            aria-label={isZh ? '查看上一则成功案例' : 'Show previous success story'}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.75} />
          </button>

          <div className="flex items-center gap-2">
            {stories.map((story, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  key={story.name}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={isZh ? `查看 ${story.name} 的成功案例` : `Show ${story.name}'s success story`}
                  aria-current={isActive ? 'true' : undefined}
                  className={`h-3 w-3 cursor-pointer rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    isActive ? 'bg-primary' : 'bg-primary-100 hover:bg-primary-200'
                  }`}
                />
              );
            })}
          </div>

          <button
            type="button"
            onClick={showNextStory}
            aria-label={isZh ? '查看下一则成功案例' : 'Show next success story'}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden"
          >
            <ChevronRight className="h-5 w-5" strokeWidth={2.75} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default TeachStepsSection;
